// Data-driven Text Animation Preset catalog. Each preset is a CONFIG PATCH applied
// onto the existing caption `config` (same keys the shared renderer reads in
// lib/animator.js + backend animation_engine.py) — so preview == export. Adding a
// preset here needs NO renderer change. Categories are open-ended.

export const CATEGORIES = [
  "Basic",
  "Trending",
  "Word After Word",
  "Letter",
  "Slide",
  "Creative",
  "3D / Texture",
];

// patch keys: animation, animationTarget, fillColor, fillGradient*, threeD*, glow*,
// shadow*, strokeEnabled/Color/Width. Anything omitted keeps the user's current value.
export const TEXT_ANIMATION_PRESETS = [
  { id: "none", name: "No Animation", category: "Basic", desc: "Static caption — no motion.",
    patch: { animation: "none" } },

  // ---- Trending (curated viral Reels/TikTok looks) ----
  { id: "trend-hormozi", name: "Hormozi Pop", category: "Trending", popular: true,
    desc: "Bold word pop with thick outline.",
    patch: { animation: "pop", animationTarget: "sentence", strokeEnabled: true, strokeColor: "#000000", strokeWidth: 6,
             fillGradientEnabled: true, fillGradientFrom: "#FFE600", fillGradientTo: "#FFB800", fillGradientAngle: 90 } },
  { id: "trend-clean-rise", name: "Clean Rise", category: "Trending",
    desc: "Minimal white words rising — creator staple.",
    patch: { animation: "rise", animationTarget: "sentence", fillColor: "#FFFFFF",
             shadow: true, shadowColor: "#000000", shadowOpacity: 0.4, shadowDy: 4, shadowBlur: 6 } },
  { id: "trend-neon-type", name: "Neon Type", category: "Trending",
    desc: "Glowing neon typewriter.",
    patch: { animation: "typewriter", animationTarget: "sentence", animEnterDur: 0.7, wordByWord: false,
             glow: true, glowColor: "#00F0FF", glowBlur: 24, fillColor: "#EAFBFF" } },
  { id: "trend-lyric", name: "Lyric Letters", category: "Trending",
    desc: "Per-letter pop for music/lyric edits.",
    patch: { letterMode: "scale", animationTarget: "sentence",
             fillGradientEnabled: true, fillGradientFrom: "#FF6AD5", fillGradientTo: "#8B5CF6", fillGradientAngle: 90 } },
  { id: "trend-slide-bold", name: "Bold Slide", category: "Trending",
    desc: "Words slide in with a heavy shadow.",
    patch: { animation: "slide-left", animationTarget: "sentence", fillColor: "#FFFFFF",
             shadow: true, shadowColor: "#000000", shadowOpacity: 0.5, shadowDy: 8, shadowBlur: 2 } },

  // ---- Word After Word ----
  { id: "word-by-word", name: "Word by Word", category: "Word After Word", popular: true,
    desc: "Each word slides up, one by one.",
    patch: { animation: "slide-up", animationTarget: "sentence" } },
  { id: "word-fade", name: "Word Fade", category: "Word After Word",
    desc: "Each word fades in elegantly.",
    patch: { animation: "fade", animationTarget: "sentence" } },
  { id: "word-blur", name: "Word Blur", category: "Word After Word",
    desc: "Each word blurs in then sharpens.",
    patch: { animation: "word-blur", animationTarget: "sentence" } },
  { id: "word-pop", name: "Word Pop", category: "Word After Word",
    desc: "Each word pops with a bounce.",
    patch: { animation: "pop", animationTarget: "sentence" } },
  { id: "word-rise", name: "Word Rise", category: "Word After Word",
    desc: "Words rise into place (Reels style).",
    patch: { animation: "rise", animationTarget: "sentence" } },
  { id: "scale-up", name: "Scale Up", category: "Word After Word",
    desc: "Each word scales from small to normal.",
    patch: { animation: "scale", animationTarget: "sentence" } },

  // ---- Letter ----
  { id: "typewriter", name: "Typewriter", category: "Letter",
    desc: "Characters appear one at a time.",
    patch: { animation: "typewriter", animationTarget: "sentence", animEnterDur: 0.9, wordByWord: false } },
  { id: "typewriter-fast", name: "Typewriter Fast", category: "Letter",
    desc: "Quicker character-by-character reveal.",
    patch: { animation: "typewriter", animationTarget: "sentence", animEnterDur: 0.5, wordByWord: false } },
  { id: "letter-fade", name: "Fade Letter", category: "Letter", popular: true,
    desc: "Each letter fades in, one by one.",
    patch: { letterMode: "fade", animationTarget: "sentence" } },
  { id: "letter-scale", name: "Scale Letter", category: "Letter",
    desc: "Each letter pops in with a scale.",
    patch: { letterMode: "scale", animationTarget: "sentence" } },

  // ---- Slide ----
  { id: "slide-left", name: "Slide In Left", category: "Slide",
    desc: "Each word slides in from the left.",
    patch: { animation: "slide-left", animationTarget: "sentence" } },
  { id: "slide-right", name: "Slide In Right", category: "Slide",
    desc: "Each word slides in from the right.",
    patch: { animation: "slide-right", animationTarget: "sentence" } },

  // ---- Creative ----
  { id: "drop", name: "Drop In", category: "Creative",
    desc: "Words drop in from the top.",
    patch: { animation: "drop", animationTarget: "sentence" } },
  { id: "wave", name: "Wave In", category: "Creative",
    desc: "Words come in with a wave.",
    patch: { animation: "wave", animationTarget: "sentence" } },
  { id: "flip", name: "Flip In", category: "Creative",
    desc: "Each word flips into place.",
    patch: { animation: "flip", animationTarget: "sentence" } },
  { id: "staggered-rise", name: "Staggered Rise", category: "Creative",
    desc: "Words rise with a staggered delay.",
    patch: { animation: "rise", animationTarget: "word" } },
  { id: "kinetic", name: "Kinetic", category: "Creative",
    desc: "Dynamic, energetic motion.",
    patch: { animation: "kinetic", animationTarget: "sentence" } },
  { id: "motion-blur", name: "Motion Blur", category: "Creative",
    desc: "Directional blur entry.",
    patch: { animation: "motion-blur", animationTarget: "sentence" } },

  // ---- 3D / Texture ----
  { id: "3d-gold", name: "3D Gold", category: "3D / Texture",
    desc: "Gold metallic 3D look.",
    patch: { animation: "pop", animationTarget: "sentence", threeD: true, threeDColor: "#7A5C00", threeDDepth: 8,
             fillGradientEnabled: true, fillGradientFrom: "#FFF3B0", fillGradientTo: "#B8860B", fillGradientAngle: 90,
             shadow: true, shadowColor: "#000000", shadowOpacity: 0.5, shadowBlur: 3 } },
  { id: "3d-silver", name: "3D Silver", category: "3D / Texture",
    desc: "Silver / chrome 3D style.",
    patch: { animation: "pop", animationTarget: "sentence", threeD: true, threeDColor: "#555555", threeDDepth: 7,
             fillGradientEnabled: true, fillGradientFrom: "#FFFFFF", fillGradientTo: "#9AA3AD", fillGradientAngle: 90 } },
  { id: "3d-neon", name: "3D Neon", category: "3D / Texture",
    desc: "Neon glow with depth.",
    patch: { animation: "fade", animationTarget: "sentence", glow: true, glowColor: "#00F0FF", glowBlur: 26, glowOpacity: 0.95,
             fillColor: "#EAFBFF", strokeEnabled: true, strokeColor: "#0090FF", strokeWidth: 3 } },
  { id: "3d-glass", name: "Glass", category: "3D / Texture",
    desc: "Glass-like transparent text.",
    patch: { animation: "fade", animationTarget: "sentence",
             fillGradientEnabled: true, fillGradientFrom: "#FFFFFF", fillGradientTo: "#BFE3FF", fillGradientAngle: 90,
             strokeEnabled: true, strokeColor: "#FFFFFF", strokeWidth: 2,
             shadow: true, shadowColor: "#0A2540", shadowOpacity: 0.35, shadowBlur: 6, shadowDy: 4 } },
  { id: "3d-stone", name: "3D Stone", category: "3D / Texture",
    desc: "Stone / concrete 3D texture.",
    patch: { animation: "pop", animationTarget: "sentence", threeD: true, threeDColor: "#2f2f2f", threeDDepth: 6,
             fillGradientEnabled: true, fillGradientFrom: "#cfcfcf", fillGradientTo: "#7a7a7a", fillGradientAngle: 90,
             shadow: true, shadowColor: "#000000", shadowOpacity: 0.5 } },
  { id: "3d-extrude", name: "3D Extrude", category: "3D / Texture",
    desc: "Bold extruded depth layers.",
    patch: { animation: "pop", animationTarget: "sentence", threeD: true, threeDColor: "#000000", threeDDepth: 12 } },
  { id: "shadow-depth", name: "Shadow Depth", category: "3D / Texture",
    desc: "Strong layered drop shadow.",
    patch: { animation: "slide-up", animationTarget: "sentence", shadow: true, shadowColor: "#000000",
             shadowDx: 0, shadowDy: 10, shadowBlur: 2, shadowOpacity: 0.55 } },
];

export function presetsByCategory() {
  const map = {};
  for (const cat of CATEGORIES) map[cat] = [];
  for (const p of TEXT_ANIMATION_PRESETS) (map[p.category] = map[p.category] || []).push(p);
  return map;
}

// Apply a preset patch onto config (non-destructive: only sets the patch's keys).
export function applyPreset(config, preset) {
  const patch = (preset && preset.patch) || {};
  const next = { ...config, ...patch, animationPresetId: preset ? preset.id : "none" };
  // Normalize the two exclusive reveal modes on EVERY apply (so switching presets resets cleanly).
  next.letterMode = patch.letterMode || "";
  if (next.letterMode) {
    next.wordByWord = false;
  } else {
    next.wordByWord = patch.wordByWord !== undefined ? patch.wordByWord : (preset && preset.id !== "none");
  }
  // karaoke special-case parity with the legacy selector
  if (patch.animation === "karaoke") next.animationTarget = "karaoke";
  return next;
}

// A tiny inline style approximating a preset for a static card preview (lightweight;
// the real live animation is in the editor preview canvas).
export function previewStyleFor(preset) {
  const p = (preset && preset.patch) || {};
  const st = { fontWeight: 800 };
  if (p.fillGradientEnabled) {
    st.background = `linear-gradient(90deg, ${p.fillGradientFrom}, ${p.fillGradientTo})`;
    st.WebkitBackgroundClip = "text";
    st.backgroundClip = "text";
    st.color = "transparent";
  } else if (p.fillColor) {
    st.color = p.fillColor;
  }
  if (p.glow) st.textShadow = `0 0 12px ${p.glowColor || "#00F0FF"}`;
  else if (p.threeD) st.textShadow = `2px 2px 0 ${p.threeDColor || "#000"}, 4px 4px 0 ${p.threeDColor || "#000"}`;
  else if (p.shadow) st.textShadow = `0 6px 6px rgba(0,0,0,.5)`;
  return st;
}
